import { useEffect, useMemo, useState } from 'react'
import { binnenState, binnenStates, buoys, istBinnen, lightStates, scenarios } from '@/core/data'
import type { Key } from '@/core/i18n'
import { locked, paywallOn, type Section } from '@/core/paywall'
import { loadProgress, type KorbId } from '@/core/progress'
import { describeCharacter, parseCharacter } from '@/core/rhythm'
import type { IalaRegion } from '@/core/settings'
import {
  buoyText,
  cfr62,
  seeAnlage1,
} from '@/core/sources'
import { nightVisionFilterSvg } from '@/render/nightVision'
import { BuoyCatalogue } from './BuoyCatalogue'
import { Cards } from './Cards'
import { BereichWahl } from './BereichWahl'
import { Bestimmen } from './Bestimmen'
import { Einstieg } from './Einstieg'
import { Koerbe } from './Koerbe'
import { MitBegriffen } from './MitBegriffen'
import { Exam } from './Exam'
import { MarkeIcon } from './Icons'
import { Paywall } from './Paywall'
import { ProgressView } from './ProgressView'
import { Scenarios } from './Scenarios'
import { SettingsView } from './SettingsView'
import { Signals } from './Signals'
import { Quiz } from './Quiz'
import { useSettings } from './settings'
import { Viewer, type Subject } from './Viewer'

/**
 * Vier Türen statt zehn.
 *
 * Vorher standen zehn gleichwertige Abschnitte in einer Leiste, benannt nach Datenarten:
 * Lichter, Seezeichen, Ausweichen, Signale, Quiz, Karten, Prüfung, Fortschritt. Ein
 * Schwarmtest hat gezeigt, was das anrichtet: der Besucher weiß nicht, wo er anfangen
 * soll, und auf dem Telefon bricht die Leiste in drei Zeilen um.
 *
 * Die Türen heißen jetzt nach Absichten. Was dahinter liegt, ist derselbe Bestand, nur
 * geordnet nach dem, was jemand gerade vorhat: üben, bestimmen, prüfen. Innerhalb einer
 * Tür steht eine zweite, kleinere Reihe, und die trägt die Datenarten.
 */
type Tuer = 'ueben' | 'bestimmen' | 'pruefen' | 'einstellungen'

const TUEREN: { id: Tuer; key: Key; abschnitte: { id: Section; key: Key }[] }[] = [
  {
    id: 'ueben',
    key: 'nav.ueben',
    abschnitte: [
      { id: 'quiz', key: 'nav.quiz' },
      { id: 'cards', key: 'nav.cards' },
      { id: 'progress', key: 'nav.progress' },
    ],
  },
  {
    id: 'bestimmen',
    key: 'nav.bestimmen',
    abschnitte: [
      { id: 'bestimmen', key: 'nav.lights' },
      { id: 'buoys', key: 'nav.buoys' },
      { id: 'scenarios', key: 'nav.scenarios' },
      { id: 'signals', key: 'nav.signals' },
    ],
  },
  { id: 'pruefen', key: 'nav.pruefen', abschnitte: [{ id: 'exam', key: 'nav.exam' }] },
  {
    id: 'einstellungen',
    key: 'nav.einstellungen',
    abschnitte: [{ id: 'settings', key: 'nav.settings' }],
  },
]

/** Zu welcher Tür ein Abschnitt gehört. */
function tuerVon(section: Section): Tuer {
  const treffer = TUEREN.find((t) => t.abschnitte.some((a) => a.id === section))
  return treffer?.id ?? 'ueben'
}

/** Die Abschnitte, in denen geübt wird: sie teilen sich Bereich und Körbe. */
const UEBEN: Section[] = ['quiz', 'cards', 'progress']

/** Was auf jedem Bildschirm zu tun ist, in einem Satz, der mit einem Verb beginnt. */
const AUFTRAEGE: Partial<Record<Section, Key>> = {
  quiz: 'ueben.auftrag',
  cards: 'cards.auftrag',
  progress: 'progress.auftrag',
  bestimmen: 'bestimmen.auftrag',
  buoys: 'buoys.auftrag',
  scenarios: 'scenarios.auftrag',
  signals: 'signals.auftrag',
  exam: 'exam.auftrag',
}

const SECTION_IDS: Section[] = [
  'bestimmen',
  'buoys',
  'scenarios',
  'signals',
  'quiz',
  'cards',
  'exam',
  'progress',
  'settings',
]

/**
 * Die Adresse darf sagen, wo es losgeht: "?state=r23-power-ge50" öffnet das Lichterbild,
 * "?buoy=cardinal-south" das Seezeichen, "?section=lichter" den Abschnitt. Die Seiten für
 * Suchmaschinen verweisen so zurück in die App, und die Belegbilder nehmen denselben Weg.
 */
function fromUrl(search: string): { section: Section; state?: string; buoy?: string } {
  const params = new URLSearchParams(search)
  const gefragt = params.get('state')
  // Eine Binnenkennung ist hier so gut wie eine Seekennung: beide zeigen ein Lichterbild.
  const state = lightStates.find((s) => s.id === gefragt)?.id ?? (gefragt && istBinnen(gefragt) ? gefragt : undefined)
  const buoy = buoys.find((b) => b.id === params.get('buoy'))?.id
  const asked = params.get('section') === 'lights' ? 'bestimmen' : params.get('section')
  const section = SECTION_IDS.find((id) => id === asked)
  return {
    section: section ?? (state ? 'bestimmen' : buoy ? 'buoys' : 'quiz'),
    ...(state ? { state } : {}),
    ...(buoy ? { buoy } : {}),
  }
}

/**
 * Der Haftungssatz, eine Zeile, ganz unten.
 *
 * Er stand vorher auf dem Startbildschirm und war damit bei jedem Aufruf zu sehen. Der
 * Startbildschirm ist weg, und der Satz war danach nur noch in zwei Detailansichten; ein
 * Verifier hat das gefunden. Der erste Versuch, ihn in die Fußzeile des Haupt-Zweigs zu
 * setzen, ließ ihn auf dem Einstiegsbildschirm fehlen, also genau dort, wo jeder Erstbesuch
 * landet und wo die Erwartung an die App entsteht. Deshalb steht er jetzt in einer eigenen
 * Komponente, die beide Zweige rendern, und nicht zweimal als Markup, das auseinanderläuft.
 */
function Haftung() {
  const { t } = useSettings()
  return (
    <footer className="shell__fuss" data-testid="haftung">
      {t('app.disclaimer')}
    </footer>
  )
}

export function App() {
  const start = useMemo(() => fromUrl(globalThis.location?.search ?? ''), [])
  const { t, pick, settings, update } = useSettings()
  const [section, setSection] = useState<Section>(start.section)
  const [stateId, setStateId] = useState(
    start.state ?? (settings.bereich === 'binnen' ? binnenStates[0]?.id : lightStates[0]?.id) ?? '',
  )
  const [buoyId, setBuoyId] = useState(start.buoy ?? buoys[0]?.id ?? '')
  const [quizSubject, setQuizSubject] = useState<Subject | undefined>()
  const [examSubject, setExamSubject] = useState<Subject | undefined>()
  const [cardSubject, setCardSubject] = useState<Subject | undefined>()
  const [scenarioId, setScenarioId] = useState(scenarios[0]?.id ?? '')
  const [scenarioSubject, setScenarioSubject] = useState<Subject | undefined>()
  // Die Kaufschranke hängt am Lernstand. Nach jeder beantworteten Frage neu ansehen.
  const [answered, setAnswered] = useState(0)
  const [korb, setKorb] = useState<KorbId | undefined>()
  const [korbWechsel, setKorbWechsel] = useState(0)

  const mode = settings.mode
  const region: IalaRegion = settings.region
  const bereich = settings.bereich
  const binnen = istBinnen(stateId)
  const state = binnen ? undefined : (lightStates.find((s) => s.id === stateId) ?? lightStates[0])
  const binnenBild = binnen ? binnenState(stateId) : undefined
  const buoy = buoys.find((b) => b.id === buoyId) ?? buoys[0]
  const gate = useMemo(
    () => locked(section, settings, loadProgress()),
    // answered zählt die Fragen mit, damit die Schranke nach der zehnten greift.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [section, settings, answered],
  )
  const subject: Subject =
    section === 'cards' && cardSubject
      ? cardSubject
      : section === 'exam' && examSubject
        ? examSubject
      : section === 'quiz' && quizSubject
        ? quizSubject
      : section === 'scenarios' && scenarioSubject
        ? scenarioSubject
        : section === 'buoys'
          ? { kind: 'buoy', id: buoy?.id ?? '' }
          : { kind: 'vessel', id: binnenBild?.id ?? state?.id ?? '' }

  /**
   * Wechselt der Bereich, muss das gezeigte Lichterbild aus dem gewählten Bestand
   * kommen. Das greift auch beim Neuladen: die Wahl steht in den Einstellungen, die
   * Kennung des Bildes kommt aus der Adresse oder aus dem Anfangswert.
   */
  useEffect(() => {
    if (section !== 'bestimmen') return
    if (bereich === 'binnen' && !istBinnen(stateId)) setStateId(binnenStates[0]?.id ?? stateId)
    if (bereich === 'see' && istBinnen(stateId)) setStateId(lightStates[0]?.id ?? stateId)
  }, [bereich, section, stateId])

  // Wechselt die Region, muss das gezeigte Zeichen in ihr auch vorkommen.
  useEffect(() => {
    if (section !== 'buoys' || !buoy) return
    if (buoy.region !== 'both' && buoy.region !== region) {
      const first = buoys.find((b) => b.region === 'both' || b.region === region)
      if (first) setBuoyId(first.id)
    }
  }, [region, section, buoy])

  useEffect(() => {
    document.body.dataset.mode = mode
  }, [mode])

  useEffect(() => {
    document.documentElement.lang = settings.lang
  }, [settings.lang])

  const character = useMemo(
    () => (buoy?.character ? describeCharacter(parseCharacter(buoy.character)) : undefined),
    [buoy?.character],
  )

  // Der Prüfungsmodus zeigt erst ein Bild, wenn eine Frage dasteht: auf dem Startschirm
  // sähe ein Fahrzeug aus wie die erste Aufgabe.
  const hasScene =
    section !== 'bestimmen' &&
    section !== 'progress' &&
    section !== 'signals' &&
    section !== 'settings' &&
    (section !== 'exam' || examSubject !== undefined) &&
    (section !== 'cards' || cardSubject !== undefined)

  /**
   * Solange niemand gesagt hat, was er können will, zeigt die App nur diese eine Frage.
   * Vorher stand hier ein Startbildschirm mit vier Kacheln, und der Schwarmtest hat
   * gezeigt, dass er die Frage "was ist das hier und für wen" unbeantwortet lässt.
   */
  if (!settings.eingerichtet) {
    return (
      <main className={`shell shell--${mode}`} data-mode={mode}>
        <Einstieg onFertig={(abschnitt) => setSection(abschnitt)} />
        <Haftung />
      </main>
    )
  }

  return (
    <main className={`shell shell--${mode}`} data-mode={mode} data-paywall={paywallOn() ? 'an' : 'aus'}>
      <span className="filters" aria-hidden="true" dangerouslySetInnerHTML={{ __html: nightVisionFilterSvg() }} />
      <header className="shell__head">
        <h1>
          <MarkeIcon />
          {t('app.title')}
        </h1>
      </header>

      <nav className="modes modes--sections" data-testid="sections">
        {TUEREN.map((tuer) => (
          <button
            key={tuer.id}
            type="button"
            className={`modes__item${tuerVon(section) === tuer.id ? ' modes__item--on' : ''}`}
            data-tuer={tuer.id}
            data-section={tuer.abschnitte[0]!.id}
            onClick={() => setSection(tuer.abschnitte[0]!.id)}
          >
            {t(tuer.key)}
          </button>
        ))}
        {section === 'buoys' && !gate && (
          <span className="regions" data-testid="regions">
            {(['A', 'B'] as const).map((r) => (
              <button
                key={r}
                type="button"
                className={`modes__item${region === r ? ' modes__item--on' : ''}`}
                data-region={r}
                onClick={() => update({ region: r })}
              >
                {t('region.button', { region: r })}
              </button>
            ))}
          </span>
        )}
      </nav>

      {/* Die zweite Reihe trägt die Datenarten der gewählten Tür. Sie steht nur da, wo
          es mehr als eine gibt: eine Reihe mit einem einzigen Knopf ist keine Wahl. */}
      {(TUEREN.find((tuer) => tuer.id === tuerVon(section))?.abschnitte.length ?? 0) > 1 && (
        <nav className="modes modes--stoff" data-testid="stoff">
          {TUEREN.find((tuer) => tuer.id === tuerVon(section))!.abschnitte.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`modes__item${section === a.id ? ' modes__item--on' : ''}`}
              data-section={a.id}
              onClick={() => setSection(a.id)}
            >
              {t(a.key)}
            </button>
          ))}
        </nav>
      )}

      {/**
        * Eine Auftragszeile für alle Abschnitte, und sie steht vor der Szene.
        *
        * Vorher trug jeder Abschnitt seine eigene, und weil die Szene darüber liegt, lag
        * der Auftrag auf einem Laptopbildschirm unter dem Rand. Ein Schwarmtest hat das
        * genau so gemeldet: "kein Satz, was ich damit tun soll". Jetzt gibt es eine
        * Stelle, und die Reihenfolge stimmt an jedem Bildschirm.
        */}
      {!gate && AUFTRAEGE[section] && (
        <MitBegriffen
          text={t(
            section === 'signals' && settings.bereich === 'binnen'
              ? 'signals.auftrag.binnen'
              : AUFTRAEGE[section]!,
          )}
          className="auftrag"
          testid="auftrag"
        />
      )}

      {/**
        * Der Bereich und die Körbe stehen über der Szene, weil die Auftragszeile darüber
        * zuerst zu ihnen einlädt: "Tippe einen Korb an". Standen sie darunter, verwies
        * die Anweisung auf etwas, das man erst suchen musste.
        */}
      {!gate && UEBEN.includes(section) && (
        <>
          <BereichWahl onWechsel={() => setKorbWechsel((n) => n + 1)} />
          <Koerbe
            aktiv={korb}
            onKorb={(gewaehlt) => {
              setKorb(gewaehlt === korb ? undefined : gewaehlt)
              setKorbWechsel((n) => n + 1)
            }}
          />
        </>
      )}

      {gate && <Paywall />}

      {!gate && hasScene && subject.kind !== 'signal' && <Viewer subject={subject} mode={mode} />}

      {!gate && section === 'bestimmen' && <Bestimmen mode={mode} start={start.state} />}
      {!gate && section === 'buoys' && <BuoyCatalogue buoyId={buoyId} region={region} onSelect={setBuoyId} />}
      {!gate && section === 'scenarios' && (
        <Scenarios scenarioId={scenarioId} onSelect={setScenarioId} onSubject={setScenarioSubject} />
      )}
      {!gate && section === 'signals' && <Signals />}
      {!gate && section === 'quiz' && (
        <Quiz
          onSubject={setQuizSubject}
          onAdvance={() => setAnswered((n) => n + 1)}
          korb={korb}
          korbWechsel={korbWechsel}
        />
      )}
      {!gate && section === 'cards' && (
        <Cards onSubject={setCardSubject} korb={korb} korbWechsel={korbWechsel} />
      )}
      {!gate && section === 'exam' && <Exam onSubject={setExamSubject} />}
      {!gate && section === 'progress' && <ProgressView />}
      {section === 'settings' && <SettingsView />}

      {!gate && section === 'buoys' && buoy && (
        <article className="detail" data-testid="buoy-detail">
          <h2>{pick(buoy.name)}</h2>
          <p className="detail__text">{pick(buoy.meaning)}</p>
          <p className="detail__text">{pick(buoy.explanation)}</p>
          {buoy.regionNote && (
            <p className="detail__text detail__note" data-testid="buoy-region-note">
              {pick(buoy.regionNote)}
            </p>
          )}
          {buoy.character && character && (
            <p className="detail__character" data-testid="buoy-character">
              <strong>{buoy.character}</strong> {pick(character)}
            </p>
          )}
          <h3 className="detail__legend">{t('detail.source')}</h3>
          <ul className="detail__quotes">
            {(buoy.cite.see ?? []).map((group) =>
              buoyText(seeAnlage1, group.section, group.ids).map((text, i) => (
                <li key={`see-${group.section}-${group.ids[i]}`}>
                  <strong>SeeSchStrO Anlage I {group.section}: </strong>
                  {text}
                </li>
              )),
            )}
            {(buoy.cite.cfr ?? []).map((group) =>
              buoyText(cfr62, group.section, group.ids).map((text, i) => (
                <li key={`cfr-${group.section}-${group.ids[i]}`}>
                  <strong>33 CFR {group.section}: </strong>
                  {text}
                </li>
              )),
            )}
          </ul>
          <p className="detail__disclaimer">
            {t('detail.disclaimer.buoys', { sources: `${seeAnlage1.title}, ${cfr62.title}` })}
          </p>
        </article>
      )}

      <Haftung />

    </main>
  )
}
